import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  // Emergent Google Auth uses an httpOnly session_token cookie. Setting
  // withCredentials on the axios instance ensures the cookie is sent on
  // every /api call. Legacy PyJWT users get Bearer via the interceptor
  // below - both transports coexist.
  withCredentials: true,
  // Bundle 92 fix - do NOT set a default `Content-Type: application/json`
  // here. axios 1.x uses `setContentTypeIfUnset` inside its FormData
  // transform, which means an instance-level default overrides the
  // per-request FormData detection and the body goes out labelled
  // `application/json`. FastAPI's form parser then can't extract the
  // `file` field and returns 422 "Field required". Leaving headers
  // empty lets axios pick the right Content-Type per body type
  // (JSON for plain objects, multipart/form-data;boundary=... for
  // FormData).
});

// Add token to requests automatically
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Bundle 92 defence-in-depth - if a caller (or a stray future
    // default) leaves a JSON Content-Type on a FormData body, axios
    // ships the FormData with the wrong header and the origin 422s
    // on the missing `file` field. Delete the header entirely when
    // the body is a FormData so the browser can regenerate it with
    // the correct boundary parameter.
    if (typeof FormData !== 'undefined'
          && config.data instanceof FormData) {
      if (config.headers) {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// F2.e - progression delta bus. The backend middleware injects
// `_progression_delta` into JSON dict responses whenever a user action
// crosses a level/rank boundary or unlocks achievements. We fan out to
// window CustomEvents so any mounted `<ProgressionEventListener>` can
// react without prop-drilling.
const emitProgressionDelta = (delta) => {
  if (!delta || typeof delta !== 'object') return;
  const hasSignal =
    delta.leveled_up || delta.rank_changed
    || (Array.isArray(delta.unlocked_achievements) && delta.unlocked_achievements.length);
  if (!hasSignal) return;
  window.dispatchEvent(new CustomEvent('dosti:progression_delta', { detail: delta }));
};

// Handle 401 responses (unauthorized).
//
// Bundle 51.1 hotfix - do NOT force-redirect anonymous visitors. The
// pre-existing interceptor unconditionally sent every 401 to
// /auth/login, which was safe as long as no public-route component
// ever hit an authenticated endpoint. Bundle 51 mounted
// <ProfileNudgeModal /> globally, which fires
// GET /api/users/me/profile-nudge/eligibility on every page load,
// including anonymous browsing. That 401 was booting anon users to
// login, breaking the public homepage and event pages.
//
// New rule: only clear session + redirect when the client actually
// had a session (token or cached user). Truly anonymous callers
// bubble the 401 back to the caller unchanged.
//
// Bundle 52 - opt-in `silent401` config flag. Callers that KNOW
// their probe may 401 on public routes (eg. profile-nudge
// eligibility) can set `{ silent401: true }` on the request config
// and the interceptor will:
//   - never touch localStorage
//   - never redirect (even for logged-in users, since a genuine
//     session expiry on a silent probe should not yank the whole
//     page - the caller decides what to do)
// This centralises the "expected anon 401" pattern into one place
// so future speculative probes on public routes cannot trip the
// Bundle 51 class of bug again.
api.interceptors.response.use(
  (response) => {
    try {
      emitProgressionDelta(response?.data?._progression_delta);
    } catch (_) { /* never break the response flow */ }
    return response;
  },
  (error) => {
    // Bundle 97 - detect Cloudflare-style non-JSON error responses
    // on media endpoints. When the origin returns 520/524, the
    // body is HTML like `<html>...</html>` and axios (with
    // `responseType: 'json'`) fails to parse it, leaving
    // `error.response.data` as a string or undefined. Surface a
    // clean structured error the FE uploader can render as
    // "Origin error - retry or use a smaller image".
    try {
      const url = error.config?.url || '';
      const isMedia = url.includes('/media/upload');
      const raw = error.response?.data;
      const looksLikeHTML = typeof raw === 'string'
        && raw.trimStart().startsWith('<');
      if (isMedia && (looksLikeHTML
                      || error.response?.status >= 520)) {
        // Overwrite with a structured payload the caller can read.
        error.response.data = {
          error: 'cf_origin_error',
          message: (
            'Origin returned an unexpected response. Please retry '
            + 'or use a smaller image.'
          ),
          cf_ray: (error.response.headers?.['cf-ray']
                   || error.response.headers?.['CF-Ray']
                   || null),
          http_status: error.response.status || 0,
        };
      }
    } catch (_) { /* never break the reject flow */ }

    if (error.response?.status === 401) {
      // Bundle 52 - opt-in silence flag. When set, the caller has
      // acknowledged that a 401 is a normal outcome for this request
      // and will handle it themselves. We do not touch storage or
      // redirect.
      if (error.config?.silent401) {
        return Promise.reject(error);
      }
      const hadSession = !!(
        localStorage.getItem('token')
        || localStorage.getItem('user')
        || localStorage.getItem('auth_token')
      );
      if (hadSession) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('access_token');
        const onAuthPage = typeof window !== 'undefined'
          && (window.location.pathname.startsWith('/auth')
              || window.location.pathname === '/login');
        if (!onAuthPage) {
          window.location.href = '/auth/login';
        }
      }
      // Anonymous 401: let the caller decide. Do not touch storage,
      // do not redirect, do not break public browsing.
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  refreshToken: async () => {
    const response = await api.post('/auth/refresh');
    return response.data;
  },
};

export default api;
